/**
 * useAgentConnection - Simplified hook for fetching LiveKit connection details
 *
 * With the high-level hooks approach, this hook now only handles:
 * 1. Fetching connection token from backend
 * 2. Token refresh logic
 *
 * The actual room connection is handled by LiveKit's RoomProvider component.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { CONNECTION, ENDPOINTS } from '../lib/constants'
import type { ConnectionDetails } from '../lib/types'
import { buildEndpoint, isTokenExpired } from '../lib/utils'

interface UseAgentConnectionOptions {
  agentId: string
  authMode?: 'internal' | 'external'
  apiKey?: string
  apiBaseUrl?: string
  onError?: (error: Error) => void
}

interface UseAgentConnectionSimpleReturn {
  connectionDetails: ConnectionDetails | null
  error: Error | null
  isLoading: boolean
  fetchToken: () => Promise<void>
}

/**
 * Simplified hook for fetching LiveKit connection token
 * Room connection is handled by RoomProvider
 */
export function useAgentConnection({ agentId, authMode = 'internal', apiKey, apiBaseUrl, onError }: UseAgentConnectionOptions): UseAgentConnectionSimpleReturn {
  const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Ref for token refresh
  const tokenRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Fetch connection details from backend
   * CRITICAL: This properly uses agentId, authMode, and apiKey parameters
   */
  const fetchConnectionDetails = useCallback(async (): Promise<ConnectionDetails> => {
    const endpoint = buildEndpoint(ENDPOINTS.LIVEKIT_TOKEN, { agentId })

    // Prepend apiBaseUrl for external embedding, or use relative path for internal
    const fullUrl = apiBaseUrl ? `${apiBaseUrl}${endpoint}` : endpoint

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // Add auth headers based on mode
    if (authMode === 'external' && apiKey) {
      headers['X-Embed-Key'] = apiKey
    }

    const body = JSON.stringify({
      agent_id: agentId, // CRITICAL: Include agent ID in request
      auth_mode: authMode,
      ...(apiKey && { api_key: apiKey }),
    })

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body,
        credentials: authMode === 'internal' ? 'include' : 'omit',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      // Handle xAI Realtime backend — no LiveKit token needed
      if (data.backend === 'xai_realtime') {
        return {
          token: '',
          serverUrl: '',
          roomName: data.room_name || '',
          participantName: '',
          participantIdentity: '',
          expiresAt: new Date(Date.now() + 3600000).toISOString(),
          backend: 'xai_realtime',
          bridgeUrl: data.bridge_url,
          voiceId: data.voice_id,
          instructions: data.instructions,
          language: data.language,
        }
      }

      // Handle Gemini Live backend
      if (data.backend === 'gemini_live') {
        return {
          token: '',
          serverUrl: '',
          roomName: '',
          participantName: '',
          participantIdentity: '',
          expiresAt: new Date(Date.now() + 3600000).toISOString(),
          backend: 'gemini_live',
          bridgeUrl: data.bridge_url,
          instructions: data.instructions,
          voiceId: data.voice_uuid,
          agentConfig: data.agent_config,
        }
      }

      // Map backend response to ConnectionDetails (LiveKit)
      return {
        token: data.token,
        serverUrl: data.server_url,
        roomName: data.room_name,
        participantName: data.participant_name,
        participantIdentity: data.participant_identity,
        expiresAt: data.expires_at,
        agentConfig: data.agent_config,
        backend: 'livekit',
      }
    } catch (error) {
      console.error('Failed to fetch connection details:', error)
      throw error instanceof Error ? error : new Error('Failed to fetch connection details')
    }
  }, [agentId, authMode, apiKey, apiBaseUrl])

  /**
   * Schedule token refresh before expiry
   */
  const scheduleTokenRefresh = useCallback(
    (details: ConnectionDetails) => {
      // Clear existing timeout
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current)
      }

      // Parse expiry time
      const expiryTime = new Date(details.expiresAt).getTime()
      const now = Date.now()
      const timeUntilExpiry = expiryTime - now

      // Schedule refresh 1 minute before expiry
      const refreshTime = Math.max(0, timeUntilExpiry - CONNECTION.TOKEN_REFRESH_BUFFER)

      if (refreshTime > 0) {
        tokenRefreshTimeoutRef.current = setTimeout(async () => {
          console.log('Refreshing LiveKit token...')
          try {
            const newDetails = await fetchConnectionDetails()
            setConnectionDetails(newDetails)
            scheduleTokenRefresh(newDetails)
          } catch (error) {
            console.error('Failed to refresh token:', error)
            setError(error as Error)
            onError?.(error as Error)
          }
        }, refreshTime)
      }
    },
    [fetchConnectionDetails, onError],
  )

  /**
   * Fetch token and set up refresh
   */
  const fetchToken = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const details = await fetchConnectionDetails()
      setConnectionDetails(details)

      // Schedule token refresh
      scheduleTokenRefresh(details)
    } catch (error) {
      console.error('Failed to fetch token:', error)
      const connectionError = error instanceof Error ? error : new Error('Failed to fetch connection details')
      setError(connectionError)
      onError?.(connectionError)
    } finally {
      setIsLoading(false)
    }
  }, [fetchConnectionDetails, scheduleTokenRefresh, onError])

  /**
   * Fetch token on mount
   */
  useEffect(() => {
    fetchToken()

    // Cleanup on unmount
    return () => {
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current)
        tokenRefreshTimeoutRef.current = null
      }
    }
  }, [fetchToken]) // Only run on mount/unmount

  /**
   * Monitor token expiry and refresh if needed
   */
  useEffect(() => {
    if (!connectionDetails?.token) return

    const checkTokenExpiry = () => {
      if (isTokenExpired(connectionDetails.token, CONNECTION.TOKEN_REFRESH_BUFFER)) {
        console.log('Token expired or about to expire, refreshing...')
        fetchToken()
      }
    }

    // Check token expiry every minute
    const interval = setInterval(checkTokenExpiry, 60000)

    return () => clearInterval(interval)
  }, [connectionDetails, fetchToken])

  return {
    connectionDetails,
    error,
    isLoading,
    fetchToken,
  }
}
